import { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { Iconify } from 'src/components/iconify';
import { PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';
import { stUploadAvatar } from '../../services/socialTradingService';

const MAX_SIZE = 5 * 1024 * 1024;

/** Each upload endpoint has its own Casbin requirement, so the button follows the path it posts to. */
const UPLOAD_PERMISSIONS: Record<string, {resource: string; action: string}[]> = {
  // POST /social-trading/upload
  '/upload': [
    {resource: 'st-rooms', action: 'create'},
    {resource: 'st-agents', action: 'create'},
  ],
  // POST /social-trading/identity/upload
  '/identity/upload': [
    {resource: 'st-accounts', action: 'create'},
    {resource: 'st-users', action: 'create'},
  ],
  // POST /social-trading/ledger/upload
  '/ledger/upload': [{resource: 'st-system-configs', action: 'update'}],
};
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp';

function isAllowedImage(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (mime) return ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(mime);
  return /\.(png|jpe?g|gif|webp)$/i.test(file.name);
}

type Props = {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  disabled?: boolean;
  /** Default avatar URLs shown as clickable thumbnails. */
  defaultUrls?: string[];
  /**
   * Upload API path under /social-trading.
   * - `/upload` integration (rooms/agents)
   * - `/identity/upload` users/accounts
   * - `/ledger/upload` avatar-defaults settings
   */
  uploadPath?: '/upload' | '/identity/upload' | '/ledger/upload';
};

export function SocialTradingAvatarField({
  value,
  onChange,
  label = 'avatar',
  disabled,
  defaultUrls,
  uploadPath = '/upload',
}: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { permissions } = usePermissions<PermissionData>();
  const canUpload = (UPLOAD_PERMISSIONS[uploadPath] || []).some((p) =>
    hasPermission(permissions?.permissions, p.resource, p.action)
  );
  // Picking a preset or clearing the field only changes local form state — the surrounding form's own
  // Save decides whether that sticks — so those follow `disabled` alone. Only the controls that actually
  // POST to the upload endpoint need the upload permission.
  const locked = !!disabled;
  const uploadLocked = locked || !canUpload;

  const validate = (file: File): string | null => {
    if (!isAllowedImage(file)) {
      return t('module.socialTrading.avatarInvalidType');
    }
    if (file.size > MAX_SIZE) {
      return t('module.socialTrading.avatarTooLarge');
    }
    return null;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validate(file);
    if (err) {
      notify(err, { type: 'warning' });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const uploadedUrl = await stUploadAvatar(file, uploadPath);
      if (!uploadedUrl) {
        notify(t('module.socialTrading.avatarUploadNoUrl'), { type: 'error' });
        return;
      }
      onChange(uploadedUrl);
      notify(t('module.socialTrading.avatarUploadOk'), { type: 'success' });
    } catch (error: any) {
      notify(error?.message || t('module.socialTrading.avatarUploadFail'), {
        type: 'error',
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const defaults = (defaultUrls || []).filter((u) => !!u && u.trim());

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        disabled={uploadLocked || uploading}
        onChange={handleUpload}
      />
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          onClick={() => !uploadLocked && !uploading && inputRef.current?.click()}
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            border: '1px dashed',
            borderColor: 'grey.400',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploadLocked || uploading ? 'not-allowed' : 'pointer',
            bgcolor: 'grey.100',
            flexShrink: 0,
            '&:hover': uploadLocked || uploading ? undefined : { borderColor: 'primary.main' },
          }}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : value ? (
            <Box component="img" src={value} alt="avatar" sx={{ width: 1, height: 1, objectFit: 'cover' }} />
          ) : (
            <Iconify icon="solar:camera-add-bold" width={24} color="text.disabled" />
          )}
        </Box>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1}>
            {canUpload && (
            <Button
              size="small"
              variant="outlined"
              disabled={uploadLocked || uploading}
              onClick={() => inputRef.current?.click()}
            >
              {value ? t('module.socialTrading.avatarReplace') : t('module.socialTrading.avatarUpload')}
            </Button>
            )}
            {!!value && (
              <Button size="small" color="inherit" disabled={locked || uploading} onClick={() => onChange('')}>
                {t('module.socialTrading.avatarClear')}
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('module.socialTrading.avatarHint')}
          </Typography>
        </Stack>
      </Stack>
      {defaults.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('module.socialTrading.avatarPickDefault')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {defaults.map((url) => {
              const selected = value === url;
              return (
                <Box
                  key={url}
                  component="img"
                  src={url}
                  alt="default"
                  onClick={() => !locked && !uploading && onChange(url)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    cursor: locked || uploading ? 'not-allowed' : 'pointer',
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'transparent',
                    opacity: locked || uploading ? 0.5 : 1,
                    '&:hover': locked || uploading ? undefined : { borderColor: 'primary.light' },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
